import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { array, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { getEditListingPageName } from 'util/urlHelpers';
import classNames from 'classnames';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { withViewport } from '../../util/contextHelpers';
import {
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPES,
} from '../../util/urlHelpers';
import { LISTING_TYPES } from 'util/constants';
import { ensureCurrentUser, ensureListing } from '../../util/data';
import { Modal, NamedRedirect, Tabs, StripeConnectAccountStatusBox } from '../../components';
import { StripeConnectAccountForm } from '../../forms';
import EditListingWizardTab from './EditListingWizardTab';
import {
  AVAILABILITY,
  DESCRIPTION,
  FEATURES,
  DETAILS,
  PRICESTOCK,
  DELIVERY,
  POLICY,
  LOCATION,
  PRICING,
  PHOTOS,
  CERTIFICATES,
  EXPECTATIONS,
  SERVICES,
  SOCIALS,
  FAQ,
  EDUCATION,
  INTODUCTORY,
  BLOGS,
} from 'util/constants';
import css from './EditListingWizard.css';
import { getTabs, TABS } from 'components/EditListingWizard/helpers';

// Tabs are horizontal in small screens
const MAX_HORIZONTAL_NAV_SCREEN_WIDTH = 1023;

////////////
// Portal //
////////////

// TODO: change all the modals to use portals at some point.
// Portal is used here to circumvent the problems that rise
// from different levels of z-indexes in DOM tree.
// Note: React Portal didn't exist when we originally created modals.

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    // The portal element is inserted in the DOM tree after
    // the Modal's children are mounted, meaning that children
    // will be mounted on a detached DOM node. If a child
    // component requires to be attached to the DOM tree
    // immediately when mounted, for example to measure a
    // DOM node, or uses 'autoFocus' in a descendant, add
    // state to Modal and only render the children when Modal
    // is inserted in the DOM tree.
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

const tabLabel = (intl, tab) => {
  let key = null;
  if (tab === DESCRIPTION) {
    key = 'EditListingWizard.tabLabelDescription';
  } else if (tab === DETAILS) {
    key = 'EditListingWizard.tabLabelDetails';
  } else if (tab === PRICESTOCK) {
    key = 'EditListingWizard.tabLabelPricingStock';
  } else if (tab === DELIVERY) {
    key = 'EditListingWizard.tabLabelDelivery';
  } else if (tab === FEATURES) {
    key = 'EditListingWizard.tabLabelFeatures';
  } else if (tab === POLICY) {
    key = 'EditListingWizard.tabLabelPolicy';
  } else if (tab === LOCATION) {
    key = 'EditListingWizard.tabLabelLocation';
  } else if (tab === PRICING) {
    key = 'EditListingWizard.tabLabelPricing';
  } else if (tab === CERTIFICATES) {
    key = 'EditListingWizard.tabLabelCertificates';
  } else if (tab === EDUCATION) {
    key = 'EditListingWizard.tabLabelEducation';
  } else if (tab === INTODUCTORY) {
    key = 'EditListingWizard.tabLabelIntroductory';
  } else if (tab === EXPECTATIONS) {
    key = 'EditListingWizard.tabLabelExpectations';
  } else if (tab === SERVICES) {
    key = 'EditListingWizard.tabLabelServices';
  } else if (tab === SOCIALS) {
    key = 'EditListingWizard.tabLabelSocials';
  } else if (tab === FAQ) {
    key = 'EditListingWizard.tabLabelFAQ';
  } else if (tab === BLOGS) {
    key = 'EditListingWizard.tabLabelBlogs';
  } else if (tab === AVAILABILITY) {
    key = 'EditListingWizard.tabLabelAvailability';
  } else if (tab === PHOTOS) {
    key = 'EditListingWizard.tabLabelPhotos';
  }

  return intl.formatMessage({ id: key });
};

/**
 * Check if a wizard tab is completed.
 *
 * @param tab wizard's tab
 * @param listing is contains some specific data if tab is completed
 *
 * @return true if tab / step is completed.
 */
const tabCompleted = (tab, listing) => {
  const {
    availabilityPlan,
    description,
    geolocation,
    price,
    title,
    publicData,
  } = listing.attributes;
  const images = listing.images;
  const deliveryOptionPicked =
    publicData && (publicData.shippingEnabled || publicData.pickupEnabled);
  switch (tab) {
    case DESCRIPTION:
      return !!(description && title);
    case DETAILS:
      return !!(description && title);
    case DELIVERY:
      return !!deliveryOptionPicked;
    case PRICESTOCK:
      return !!price;
    case FEATURES:
      return !!(publicData && publicData.sports);
    case POLICY:
      return !!(publicData && typeof publicData.rules !== 'undefined');
    case LOCATION:
      return !!(geolocation && publicData && publicData.location && publicData.location.address);
    case PRICING:
      return !!price;
    case CERTIFICATES:
      return !!publicData.certificates;
    case EXPECTATIONS:
      return !!publicData.expectations;
    case FAQ:
      return !!publicData.FAQ;
    case SERVICES:
      return !!publicData.services;
    case SOCIALS:
      return !!publicData.socials;
    case EDUCATION:
      return !!publicData.education && !!publicData.work_experience;
    case INTODUCTORY:
      return !!publicData.introductoryVideo;
    case BLOGS:
      return !!publicData.blogs;
    case AVAILABILITY:
      return !!availabilityPlan;
    case PHOTOS:
      return images && images.length > 0;
    default:
      return false;
  }
};

/**
 * Check which wizard tabs are active and which are not yet available. Tab is active if previous
 * tab is completed. In edit mode all tabs are active.
 *
 * @param isNew flag if a new listing is being created or an old one being edited
 * @param listing data to be checked
 *
 * @return object containing activity / editability of different tabs of this wizard
 */
const tabsActive = (isNew, listing, tabs) => {
  return tabs.reduce((acc, tab) => {
    const previousTabIndex = tabs.findIndex(t => t === tab) - 1;
    const isActive =
      previousTabIndex >= 0 ? !isNew || tabCompleted(tabs[previousTabIndex], listing) : true;
    return { ...acc, [tab]: isActive };
  }, {});
};

const scrollToTab = (tabPrefix, tabId, type) => {
  if (LISTING_TYPES.ENQUIRY === type && tabId === DESCRIPTION) return;

  const el = document.querySelector(`#${tabPrefix}_${tabId}`);
  if (el) {
    el.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }
};

// Create return URL for the Stripe onboarding form
const createReturnURL = (returnURLType, rootURL, routes, pathParams) => {
  const path = createResourceLocatorString(
    'EditListingStripeOnboardingPage',
    routes,
    { ...pathParams, returnURLType },
    {}
  );
  const root = rootURL.replace(/\/$/, '');
  return `${root}${path}`;
};

// Get attribute: stripeAccountData
const getStripeAccountData = stripeAccount => stripeAccount.attributes.stripeAccountData || null;

// Get last 4 digits of bank account returned in Stripe account
const getBankAccountLast4Digits = stripeAccountData =>
  stripeAccountData && stripeAccountData.external_accounts.data.length > 0
    ? stripeAccountData.external_accounts.data[0].last4
    : null;

// Check if there's requirements on selected type: 'past_due', 'currently_due' etc.
const hasRequirements = (stripeAccountData, requirementType) =>
  stripeAccountData != null &&
  stripeAccountData.requirements &&
  Array.isArray(stripeAccountData.requirements[requirementType]) &&
  stripeAccountData.requirements[requirementType].length > 0;

// Redirect user to Stripe's hosted Connect account onboarding form
const handleGetStripeConnectAccountLinkFn = (getLinkFn, commonParams) => type => () => {
  getLinkFn({ type, ...commonParams })
    .then(url => {
      if (typeof window !== 'undefined') window.location.href = url;
    })
    .catch(err => console.error(err));
};

// Create a new or edit listing through EditListingWizard
class EditListingWizard extends Component {
  constructor(props) {
    super(props);

    // Having this info in state would trigger unnecessary rerendering
    this.hasScrolledToTab = false;

    this.state = {
      draftId: null,
      showPayoutDetails: false,
      portalRoot: null,
    };
    this.postRequestGifRef = React.createRef();
    this.handleCreateFlowTabScrolling = this.handleCreateFlowTabScrolling.bind(this);
    this.handlePublishListing = this.handlePublishListing.bind(this);
    this.handlePayoutModalClose = this.handlePayoutModalClose.bind(this);
    this.handlePayoutSubmit = this.handlePayoutSubmit.bind(this);
  }

  componentDidMount() {
    const { stripeOnboardingReturnURL } = this.props;

    if (stripeOnboardingReturnURL != null) {
      this.setState({ showPayoutDetails: true });
    }

    if (this.postRequestGifRef.current) {
      this.postRequestGifRef.current.src =
        '/static/images/landingpage/landingpage_postRequestGif.gif';
    }
  }

  handleCreateFlowTabScrolling(shouldScroll) {
    this.hasScrolledToTab = shouldScroll;
  }

  handlePublishListing(id) {
    const { type, onPublishListingDraft, currentUser, stripeAccount } = this.props;

    const stripeConnected =
      currentUser && currentUser.stripeAccount && !!currentUser.stripeAccount.id;

    const stripeAccountData = stripeConnected ? getStripeAccountData(stripeAccount) : null;

    const requirementsMissing =
      stripeAccount &&
      (hasRequirements(stripeAccountData, 'past_due') ||
        hasRequirements(stripeAccountData, 'currently_due'));

    // Enable if you want to verify strip account before publish Listing
    if (
      (stripeConnected && !requirementsMissing) ||
      type === LISTING_TYPES.ENQUIRY ||
      type === LISTING_TYPES.COMPANY ||
      type === LISTING_TYPES.FREELANCER ||
      type === LISTING_TYPES.PLAYER_PROFILE ||
      type === LISTING_TYPES.ACADEMY ||
      type === LISTING_TYPES.REMOTE_TUTOR ||
      type === LISTING_TYPES.TUTOR
    ) {
      onPublishListingDraft(id);
    } else {
      this.setState({
        draftId: id,
        showPayoutDetails: true,
      });
    }
  }

  handlePayoutModalClose() {
    this.setState({ showPayoutDetails: false });
    this.props.onPublishListingDraft(this.state.draftId);
  }

  handlePayoutSubmit(values) {
    this.props
      .onPayoutDetailsSubmit(values)
      .then(response => {
        this.props.onManageDisableScrolling('EditListingWizard.payoutModal', false);
        this.props.onPublishListingDraft(this.state.draftId);
      })
      .catch(() => {
        // do nothing
      });
  }

  render() {
    const {
      id,
      type,
      className,
      rootClassName,
      params,
      listing,
      viewport,
      intl,
      errors,
      fetchInProgress,
      payoutDetailsSaveInProgress,
      payoutDetailsSaved,
      onManageDisableScrolling,
      onPayoutDetailsFormChange,
      onGetStripeConnectAccountLink,
      getAccountLinkInProgress,
      createStripeAccountError,
      updateStripeAccountError,
      fetchStripeAccountError,
      stripeAccountFetched,
      stripeAccount,
      currentUser,
      isEducationSite,
      isTennisSite,
      ...rest
    } = this.props;
    const tabs = getTabs(type);

    const selectedTab = params.tab;
    const isNewListingFlow = [LISTING_PAGE_PARAM_TYPE_NEW, LISTING_PAGE_PARAM_TYPE_DRAFT].includes(
      params.type
    );
    const rootClasses = rootClassName || css.root;
    const classes = classNames(rootClasses, className);
    const currentListing = ensureListing(listing);
    const tabsStatus = tabsActive(isNewListingFlow, currentListing, tabs);

    // If selectedTab is not active, redirect to the beginning of wizard
    if (!tabsStatus[selectedTab]) {
      const currentTabIndex = tabs.indexOf(selectedTab);
      const nearestActiveTab = tabs
        .slice(0, currentTabIndex)
        .reverse()
        .find(t => tabsStatus[t]);

      return (
        <NamedRedirect
          name={getEditListingPageName(type)}
          params={{ ...params, tab: nearestActiveTab }}
        />
      );
    }

    const { width } = viewport;
    const hasViewport = width > 0;
    const hasHorizontalTabLayout = hasViewport && width <= MAX_HORIZONTAL_NAV_SCREEN_WIDTH;
    const hasVerticalTabLayout = hasViewport && width > MAX_HORIZONTAL_NAV_SCREEN_WIDTH;
    const hasFontsLoaded =
      hasViewport && document.documentElement.classList.contains('fontsLoaded');

    // Check if scrollToTab call is needed (tab is not visible on mobile)
    if (hasVerticalTabLayout) {
      this.hasScrolledToTab = true;
    } else if (hasHorizontalTabLayout && !this.hasScrolledToTab && hasFontsLoaded) {
      const tabPrefix = id;
      scrollToTab(tabPrefix, selectedTab, type);
      this.hasScrolledToTab = true;
    }

    const tabLink = tab => {
      const links = {
        [LISTING_TYPES.LISTING]: 'EditListingPage',
        [LISTING_TYPES.ENQUIRY]: 'EditInquirePage',
        [LISTING_TYPES.COMPANY]: 'EditCompanyPage',
        [LISTING_TYPES.FREELANCER]: 'EditFreelancerPage',
        [LISTING_TYPES.CLASS]: 'EditClassPage',
        [LISTING_TYPES.FACILITY]: 'EditFacilityPage',
        [LISTING_TYPES.EVENT]: 'EditEventPage',
        [LISTING_TYPES.PRODUCT]: 'EditProductPage',
        [LISTING_TYPES.MEMBERSHIP]: 'EditMembershipPage',
        [LISTING_TYPES.ACADEMY]: 'EditAcademyPage',
        [LISTING_TYPES.COURSES]: 'EditCoursesPage',
        [LISTING_TYPES.EDUCATION_COURSES]: 'EditEducationCoursesPage',
        [LISTING_TYPES.HOURLY_SESSION]: 'EditHourlySessionPage',
        [LISTING_TYPES.TUTOR]: 'EditTutorPage',
        [LISTING_TYPES.PLAYER_PROFILE]: 'EditPlayerProfilePage',
        [LISTING_TYPES.REMOTE_TUTOR]: 'EditRemoteTutorPage',
        [LISTING_TYPES.PLAYER_PROFILE]: 'EditPlayerProfilePage',
      };
      return {
        name: links[type],
        params: { ...params, tab },
      };
    };

    const setPortalRootAfterInitialRender = () => {
      if (!this.state.portalRoot) {
        this.setState({ portalRoot: document.getElementById('portal-root') });
      }
    };
    const formDisabled = getAccountLinkInProgress;
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const currentUserLoaded = !!ensuredCurrentUser.id;
    const stripeConnected = currentUserLoaded && !!stripeAccount && !!stripeAccount.id;

    const rootURL = config.canonicalRootURL;
    const routes = routeConfiguration();
    const { returnURLType, ...pathParams } = params;
    const successURL = createReturnURL('success', rootURL, routes, pathParams);
    const failureURL = createReturnURL('failure', rootURL, routes, pathParams);

    const accountId = stripeConnected ? stripeAccount.id : null;
    const stripeAccountData = stripeConnected ? getStripeAccountData(stripeAccount) : null;

    const requirementsMissing =
      stripeAccount &&
      (hasRequirements(stripeAccountData, 'past_due') ||
        hasRequirements(stripeAccountData, 'currently_due'));

    const savedCountry = stripeAccountData ? stripeAccountData.country : null;

    const handleGetStripeConnectAccountLink = handleGetStripeConnectAccountLinkFn(
      onGetStripeConnectAccountLink,
      {
        accountId,
        successURL,
        failureURL,
      }
    );

    const returnedNormallyFromStripe = returnURLType === 'success';
    const showVerificationError = returnURLType === 'failure';
    const showVerificationNeeded = stripeConnected && requirementsMissing;

    const getTabIcon = tab => {
      switch (tab) {
        case DESCRIPTION:
          return (
            <svg
              width="14"
              height="16"
              viewBox="0 0 14 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M8.8 1H4.6C2.61178 1 1 2.567 1 4.5V11.5C1 13.433 2.61178 15 4.6 15H9.4C11.3882 15 13 13.433 13 11.5V5.08333M8.8 1H9.25442C9.73181 1 10.1896 1.18437 10.5272 1.51256L12.4728 3.4041C12.8104 3.73229 13 4.17741 13 4.64154V5.08333M8.8 1V3.33333C8.8 4.29983 9.60589 5.08333 10.6 5.08333H13M3.4 6.83333H7M3.4 10.1667H10.3"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case DETAILS:
          return (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="21"
              viewBox="0 0 22 21"
              fill="none"
              className={css.editWizardSvg}
            >
              <g clipPath="url(#clip0)">
                <path
                  d="M14.7516 0.180664C14.7607 0.220344 14.7668 0.260669 14.7698 0.301289C14.7713 0.700247 14.7698 1.09921 14.7698 1.51191C14.8146 1.51421 14.8538 1.51796 14.893 1.51796C15.4503 1.51796 16.0074 1.51796 16.5645 1.51796C17.2039 1.51796 17.7182 1.79671 18.1408 2.26441C18.5372 2.70358 18.7157 3.22275 18.7099 3.82296C18.698 5.22879 18.706 6.63546 18.706 8.04108V8.179H1.64998V8.30566C1.64998 11.0344 1.64998 13.7636 1.64998 16.4932C1.64998 16.7788 1.70274 17.048 1.92798 17.248C2.11667 17.4159 2.34888 17.4825 2.59034 17.4915C3.03054 17.508 3.47157 17.5092 3.91218 17.509C5.67546 17.5077 7.43867 17.505 9.20181 17.5009C9.3213 17.5009 9.441 17.5069 9.5605 17.5094C10.049 17.52 10.3323 17.9246 10.1832 18.3984C10.0888 18.698 9.89209 18.8444 9.58103 18.8444C7.23287 18.8444 4.88457 18.8449 2.53613 18.8459C1.76886 18.8471 1.18698 18.4986 0.738773 17.8923C0.459129 17.5127 0.335938 17.0827 0.335938 16.6065C0.339496 12.3226 0.339839 8.03844 0.336964 3.754C0.336964 3.08733 0.600799 2.54421 1.07385 2.09879C1.49804 1.69941 1.99491 1.49629 2.58274 1.514C3.10179 1.53025 3.62186 1.51733 4.14131 1.51733H4.27354V1.35379C4.27354 1.00316 4.27354 0.652539 4.27354 0.301914C4.27639 0.261139 4.28208 0.220621 4.29058 0.180664L5.58286 0.180664V1.50962H13.4593V0.180664H14.7516ZM13.4581 2.859H5.58491V4.84275H4.27395V2.85962C4.25406 2.85585 4.23397 2.85321 4.21379 2.85171C3.6736 2.85171 3.13341 2.84941 2.59342 2.85358C2.41097 2.8532 2.23245 2.90733 2.08012 3.00921C1.77543 3.21296 1.65141 3.51191 1.64998 3.87066C1.64833 4.81858 1.64833 5.7665 1.64998 6.71441C1.64998 6.84921 1.64998 6.84921 1.78343 6.84921H17.3877C17.3901 6.80275 17.3934 6.769 17.3934 6.73504C17.3934 5.78087 17.3934 4.82657 17.3934 3.87212C17.3922 3.25525 16.9959 2.85316 16.391 2.85129C15.8884 2.84983 15.385 2.85129 14.8834 2.85129C14.8501 2.85129 14.8169 2.85441 14.7705 2.85671V2.98483C14.7705 3.57171 14.769 4.15837 14.7723 4.74525C14.7723 4.82733 14.7497 4.85212 14.6684 4.85108C14.2988 4.84712 13.9293 4.84962 13.5607 4.84921C13.531 4.84921 13.5012 4.84504 13.4581 4.84191V2.859Z"
                  fill="#1F293D"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M15.4601 20.1486C15.5452 20.1588 15.6303 20.1689 15.715 20.1815H16.4527C16.5362 20.1693 16.62 20.1586 16.7038 20.1479C16.8878 20.1245 17.0716 20.1011 17.2522 20.0617C18.0451 19.89 18.7461 19.5215 19.3723 19.0027C20.0868 18.4107 20.6276 17.6854 20.9787 16.8177C21.1697 16.3491 21.2839 15.8519 21.3171 15.3459C21.3197 15.3041 21.3242 15.2628 21.3288 15.2211C21.331 15.2011 21.3331 15.1809 21.3351 15.1607V14.5148C21.3302 14.4663 21.3262 14.4175 21.3221 14.3688C21.3136 14.2656 21.3051 14.1625 21.2879 14.0609C20.983 12.2527 20.0351 10.9057 18.4291 10.0723C17.0179 9.33982 15.5509 9.30419 14.0855 9.90232C12.9719 10.3569 12.1143 11.1359 11.5141 12.1923C11.0533 13.0037 10.8246 13.8816 10.8282 14.8261C10.8306 15.5084 10.9208 16.1686 11.1817 16.7979C11.8957 18.5154 13.1405 19.6025 14.9161 20.0592C15.0934 20.1049 15.2767 20.1267 15.4601 20.1486ZM18.7001 15.501V14.1785L18.5654 14.1791C18.4263 14.1791 18.2872 14.179 18.1482 14.1789C17.7136 14.1786 17.2791 14.1783 16.8436 14.181C16.7582 14.1814 16.7343 14.1562 16.7343 14.0697C16.737 13.6257 16.7367 13.1819 16.7364 12.738C16.7363 12.5959 16.7362 12.4539 16.7362 12.3118V12.1885H15.4314V15.501H18.7001Z"
                  fill="#1F293D"
                />
                <path d="M3.62305 11.5036V10.1865H8.20166V11.5036H3.62305Z" fill="#1F293D" />
                <path d="M8.20361 14.1855V15.503H3.625V14.1855H8.20361Z" fill="#1F293D" />
              </g>
              <defs>
                <clipPath id="clip0">
                  <rect
                    width="21"
                    height="20"
                    fill="white"
                    transform="translate(0.335938 0.180664)"
                  />
                </clipPath>
              </defs>
            </svg>
          );
        case FEATURES:
          return null;
        case POLICY:
          return null;
        case LOCATION:
          return (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M16.6668 8.33268C16.6668 12.0146 10.0002 18.3327 10.0002 18.3327C10.0002 18.3327 3.3335 12.0146 3.3335 8.33268C3.3335 4.65078 6.31826 1.66602 10.0002 1.66602C13.6821 1.66602 16.6668 4.65078 16.6668 8.33268Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.99984 9.91667C10.8743 9.91667 11.5832 9.2078 11.5832 8.33333C11.5832 7.4589 10.8743 6.75 9.99984 6.75C9.12536 6.75 8.4165 7.4589 8.4165 8.33333C8.4165 9.2078 9.12537 9.91667 9.99984 9.91667Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case PRICING:
        case PRICESTOCK:
          return (
            <svg
              width="13"
              height="20"
              viewBox="0 0 13 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <circle
                cx="6.50088"
                cy="6.85"
                r="1.35"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.85039 12.0469C7.54216 11.7624 6.9993 11.5645 6.50039 11.5521M5.15039 14.7469C5.44041 15.1039 5.97964 15.3075 6.50039 15.3247M6.50039 11.5521C5.90677 11.5374 5.37539 11.7852 5.37539 12.4623C5.37539 13.7085 7.85039 13.0854 7.85039 14.3316C7.85039 15.0423 7.19167 15.3477 6.50039 15.3247M6.50039 11.5521V10.8008M6.50039 15.3247V16.2008"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1 6.4L5.79941 1.68785C6.1884 1.30594 6.8116 1.30594 7.20059 1.68785L12 6.4V19H1V6.4Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case CERTIFICATES:
        case EDUCATION:
          return (
            <svg
              width="14"
              height="21"
              viewBox="0 0 14 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M11.1659 11.832V19.332L6.99919 17.2487L2.83252 19.332V11.832"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="6.99984"
                cy="7.49935"
                r="5.83333"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 5L7.94046 6.20557L9.37764 6.72746L8.52169 7.99443L8.46946 9.52254L7 9.1L5.53054 9.52254L5.47831 7.99443L4.62236 6.72746L6.05954 6.20557L7 5Z"
                stroke="#1F293D"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case EXPECTATIONS:
          return (
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 23.75V6.25C5 5.58696 5.26339 4.95107 5.73223 4.48223C6.20107 4.01339 6.83696 3.75 7.5 3.75H24.25C24.4489 3.75 24.6397 3.82902 24.7803 3.96967C24.921 4.11032 25 4.30109 25 4.5V20.8925M7.5 21.25H25M7.5 26.25H25"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M7.5 26.25C6.83696 26.25 6.20107 25.9866 5.73223 25.5178C5.26339 25.0489 5 24.413 5 23.75C5 23.087 5.26339 22.4511 5.73223 21.9822C6.20107 21.5134 6.83696 21.25 7.5 21.25"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M11.25 8.75H18.75"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          );
        case SERVICES:
          return (
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 12.25H12.875M1 12.25L6 17.875M1 12.25L6 6.625M12.875 12.25L19.125 1M12.875 12.25L19.125 21M19.125 1L11.625 2.25M19.125 1L21 7.875M19.125 21L21 14.125M19.125 21L11.625 20.375"
                stroke="black"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          );
        case SOCIALS:
          return (
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_1137_175)">
                <path
                  d="M6.24935 19.2836C8.78068 19.2836 10.8327 17.2316 10.8327 14.7003C10.8327 12.1689 8.78068 10.1169 6.24935 10.1169C3.71804 10.1169 1.66602 12.1689 1.66602 14.7003C1.66602 15.5351 1.88921 16.3178 2.27918 16.9919L1.89518 19.0544L3.95768 18.6704C4.63183 19.0604 5.41452 19.2836 6.24935 19.2836Z"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12.7352 15.8651C13.5488 15.734 14.313 15.4557 14.9999 15.0584L17.9999 15.6169L17.4413 12.6169C18.0085 11.6364 18.3332 10.4979 18.3332 9.28361C18.3332 5.60171 15.3484 2.61694 11.6665 2.61694C8.31037 2.61694 5.53345 5.09693 5.06836 8.32431"
                  stroke="black"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1137_175">
                  <rect width="20" height="20" fill="white" transform="translate(0 0.950195)" />
                </clipPath>
              </defs>
            </svg>
          );
        case INTODUCTORY:
          return (
            <svg
              width="19"
              height="16"
              viewBox="0 0 19 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M1 15V14.5C1 12.567 2.56701 11 4.5 11C6.433 11 8 12.567 8 14.5V15"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 11C6.10455 11 7 10.1046 7 9C7 7.89543 6.10455 7 5 7C3.89543 7 3 7.89543 3 9C3 10.1046 3.89543 11 5 11Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 9C16.2091 9 18 7.20912 18 5C18 2.79086 16.2091 1 14 1C11.7909 1 10 2.79086 10 5C10 5.72856 10.1948 6.41164 10.5351 7L10.2 8.8L12 8.46488C12.5883 8.8052 13.2714 9 14 9Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.6825 4.20757L14.163 3.10605C14.0963 2.96465 13.9037 2.96465 13.837 3.10605L13.3175 4.20757L12.1557 4.3853C12.0066 4.4081 11.9472 4.59987 12.0551 4.70987L12.8957 5.5667L12.6973 6.77714C12.6718 6.93259 12.8277 7.05112 12.9611 6.97771L14 6.4059L15.0389 6.97771C15.1723 7.05112 15.3282 6.93259 15.3027 6.77714L15.1044 5.5667L15.9448 4.70987C16.0528 4.59987 15.9934 4.4081 15.8443 4.3853L14.6825 4.20757Z"
                stroke="#1F293D"
                strokeWidth="0.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case FAQ:
        case BLOGS:
        case AVAILABILITY:
        case DELIVERY:
          return (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M16.6668 9.99935V4.78979C16.6668 4.65718 16.6142 4.53001 16.5204 4.43623L13.8966 1.81247C13.8028 1.71869 13.6757 1.66602 13.5431 1.66602H3.8335C3.55735 1.66602 3.3335 1.88987 3.3335 2.16602V17.8327C3.3335 18.1088 3.55735 18.3327 3.8335 18.3327H9.16683"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.6665 8.33333H13.3332M6.6665 5H9.99984M6.6665 11.6667H9.1665"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3335 4.49935V1.96064C13.3335 1.79792 13.4654 1.66602 13.6282 1.66602C13.7062 1.66602 13.7812 1.69706 13.8365 1.75231L16.5805 4.49639C16.6358 4.55164 16.6668 4.62658 16.6668 4.70472C16.6668 4.86744 16.5349 4.99935 16.3722 4.99935H13.8335C13.5573 4.99935 13.3335 4.77549 13.3335 4.49935Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.423 18.4942C11.081 17.6701 10.5069 15.9666 11.141 14.4729C11.8395 12.8274 13.7397 12.0597 15.3852 12.7581C17.0308 13.4566 17.7985 15.3569 17.1001 17.0024C16.8271 17.6454 16.3707 18.1544 15.8177 18.4942"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.7632 17.0156V18.4398C15.7632 18.5471 15.8501 18.6341 15.9574 18.6341H17.3816"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.1157 15.9863V14.9863"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14 16L13 16"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        case PHOTOS:
          return (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={css.editWizardSvg}
            >
              <path
                d="M17 3.46667V16.5333C17 16.7911 16.7911 17 16.5333 17H3.46667C3.20893 17 3 16.7911 3 16.5333V3.46667C3 3.20893 3.20893 3 3.46667 3H16.5333C16.7911 3 17 3.20893 17 3.46667Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3 13.4L8.44444 11L17 15"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.5 8C12.6716 8 12 7.32843 12 6.5C12 5.67157 12.6716 5 13.5 5C14.3284 5 15 5.67157 15 6.5C15 7.32843 14.3284 8 13.5 8Z"
                stroke="#1F293D"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          );
        default:
          return null;
      }
    };

    // Redirect from success URL to basic path for StripePayoutPage
    if (returnedNormallyFromStripe && stripeConnected && !requirementsMissing) {
      return <NamedRedirect name="EditListingPage" params={pathParams} />;
    }

    return (
      <div className={classes} ref={setPortalRootAfterInitialRender}>
        {LISTING_TYPES.ENQUIRY === type && (
          <div className={css.requestDescriptionContainer}>
            <div className={css.requestInfoWrapper}>
              <span className={css.requsetTitleBlue}>
                <FormattedMessage id="EditListingWizard.proConnect" />
              </span>
              <span className={css.requsetTitle}>
                <FormattedMessage id="EditListingWizard.perfectToolForClient" />
              </span>
              <p>
                <FormattedMessage id="EditListingWizard.lookingPrivateTraining" />
              </p>
            </div>
            <div className={css.postRequestGifContainer}>
              <img
                className={css.postRequestGif}
                ref={this.postRequestGifRef}
                src={'/static/images/landingpage/landingpage_postRequestGif.gif'}
                alt="landingpage_postRequestGif"
              />
              <img
                className={css.imagePostRequestGifBackground}
                src={'/static/images/landingpage/landingpage_postRequestGifBackground.png'}
                alt="landingpage_postRequestGifBackground"
              />
            </div>
          </div>
        )}

        <Tabs
          rootClassName={css.tabsContainer}
          navRootClassName={css.nav}
          tabRootClassName={css.tab}
          title={'create'}
          currentUser={currentUser}
        >
          {tabs.map(tab => {
            return (
              <EditListingWizardTab
                {...rest}
                key={tab}
                currentUser={ensuredCurrentUser}
                svgIcon={getTabIcon(tab)}
                listingType={type}
                tabId={`${id}_${tab}`}
                tabLabel={tabLabel(intl, tab)}
                tabLinkProps={tabLink(tab)}
                selected={selectedTab === tab}
                disabled={isNewListingFlow && !tabsStatus[tab]}
                tab={tab}
                intl={intl}
                params={params}
                listing={listing}
                marketplaceTabs={tabs}
                errors={errors}
                handleCreateFlowTabScrolling={this.handleCreateFlowTabScrolling}
                handlePublishListing={this.handlePublishListing}
                fetchInProgress={fetchInProgress}
                onManageDisableScrolling={onManageDisableScrolling}
                isTabCompleted={tabCompleted(tab, listing)}
                isEducationSite={isEducationSite}
                // isTennisSite={isTennisSite}
              />
            );
          })}
        </Tabs>
        {this.state.portalRoot && onManageDisableScrolling ? (
          <Portal portalRoot={this.state.portalRoot}>
            <Modal
              id="EditListingWizard.payoutModal"
              isOpen={this.state.showPayoutDetails}
              onClose={this.handlePayoutModalClose}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <div className={css.modalPayoutDetailsWrapper}>
                <h1 className={css.modalTitle}>
                  <FormattedMessage id="EditListingPhotosPanel.payoutModalTitleOneMoreThing" />
                  <br />
                  <FormattedMessage id="EditListingPhotosPanel.cardPayoutPreferences" />
                </h1>
                <p className={css.modalMessage}>
                  <FormattedMessage id="EditListingPhotosPanel.payoutModalInfo" />
                </p>
                <div className={css.payoutModalInfoNote}>
                  <span className={css.noteFont}>
                    <FormattedMessage id="EditListingPhotosPanel.note" />
                  </span>
                  <FormattedMessage id="EditListingPhotosPanel.payoutModalInfoNote" />
                </div>
                {!currentUserLoaded ? (
                  <FormattedMessage id="StripePayoutPage.loadingData" />
                ) : (
                  <StripeConnectAccountForm
                    disabled={formDisabled}
                    inProgress={payoutDetailsSaveInProgress}
                    ready={payoutDetailsSaved}
                    stripeBankAccountLastDigits={getBankAccountLast4Digits(stripeAccountData)}
                    savedCountry={savedCountry}
                    submitButtonText={intl.formatMessage({
                      id: 'StripePayoutPage.submitButtonText',
                    })}
                    stripeAccountError={
                      createStripeAccountError ||
                      updateStripeAccountError ||
                      fetchStripeAccountError
                    }
                    stripeAccountFetched={stripeAccountFetched}
                    onChange={onPayoutDetailsFormChange}
                    onSubmit={rest.onPayoutDetailsSubmit}
                    onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink}
                    stripeConnected={stripeConnected}
                  >
                    {stripeConnected && (showVerificationError || showVerificationNeeded) ? (
                      <StripeConnectAccountStatusBox
                        type={showVerificationError ? 'verificationError' : 'verificationNeeded'}
                        inProgress={getAccountLinkInProgress}
                        onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink(
                          'custom_account_verification'
                        )}
                      />
                    ) : stripeConnected && savedCountry ? (
                      <StripeConnectAccountStatusBox
                        type="verificationSuccess"
                        inProgress={getAccountLinkInProgress}
                        disabled={payoutDetailsSaveInProgress}
                        onGetStripeConnectAccountLink={handleGetStripeConnectAccountLink(
                          'custom_account_update'
                        )}
                      />
                    ) : null}
                  </StripeConnectAccountForm>
                )}
              </div>
            </Modal>
          </Portal>
        ) : null}
      </div>
    );
  }
}

EditListingWizard.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
  updateInProgress: false,
};

EditListingWizard.propTypes = {
  id: string.isRequired,
  type: oneOf([
    LISTING_TYPES.LISTING,
    LISTING_TYPES.ENQUIRY,
    LISTING_TYPES.COMPANY,
    LISTING_TYPES.FREELANCER,
    LISTING_TYPES.EVENT,
    LISTING_TYPES.PRODUCT,
    LISTING_TYPES.MEMBERSHIP,
    LISTING_TYPES.FACILITY,
    LISTING_TYPES.CLASS,
    LISTING_TYPES.HOURLY_SESSION,
    LISTING_TYPES.ACADEMY,
    LISTING_TYPES.TUTOR,
    LISTING_TYPES.COURSES,
    LISTING_TYPES.EDUCATION_COURSES,
    LISTING_TYPES.PLAYER_PROFILE,
  ]).isRequired,
  className: string,
  rootClassName: string,
  params: shape({
    id: string.isRequired,
    slug: string.isRequired,
    type: oneOf(LISTING_PAGE_PARAM_TYPES).isRequired,
    tab: oneOf(TABS).isRequired,
  }).isRequired,
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: shape({
    attributes: shape({
      publicData: object,
      description: string,
      geolocation: object,
      pricing: object,
      title: string,
    }),
    images: array,
  }),

  errors: shape({
    createListingDraftError: object,
    updateListingError: object,
    publishListingError: object,
    showListingsError: object,
    uploadImageError: object,
    createStripeAccountError: object,
  }).isRequired,
  fetchInProgress: bool.isRequired,
  payoutDetailsSaveInProgress: bool.isRequired,
  payoutDetailsSaved: bool.isRequired,
  onPayoutDetailsFormChange: func.isRequired,
  onPayoutDetailsSubmit: func.isRequired,
  onGetStripeConnectAccountLink: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  updateInProgress: bool,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(withViewport, injectIntl)(EditListingWizard);
